/**
 * Header of the object screen and its «Налаштування»: crumbs, name, model
 * and SN, the connection chip (D4: «Очікує доступу», «Доступ закрито», «Немає
 * права керування» — the note below says more), chips of region, DSO, tariff,
 * PV, history and (D3) an open Phage.Energy access with «Перевірити доступ»
 * (DSO, a complete history and the open access hidden on a phone), tabs
 * «Огляд» / «Налаштування».
 */
import { Link } from 'react-router-dom';
import { type SiteOverviewResponse, type SiteResponse, todayInTz } from 'shared';

import { homePath } from '../../components/AppShell.js';
import { PhageAccessChip } from '../../components/PhageAccess.js';
import { fmtDateTime, fmtNumShort, fmtSince, modelLabel, serialOf } from '../../components/format.js';
import { osrName, regionOf } from '../../components/regions.js';
import { HISTORY_DAYS, type HistState, connOf, connState } from '../../components/site-state.js';
import { useProfile } from '../../hooks/use-profile.js';
import { useLocale } from '../../i18n/locale-context.js';
import type { MessageKey } from '../../i18n/messages.js';

export function useHistText() {
  const { t, tf, tp } = useLocale();
  return (hist: HistState): string => {
    switch (hist.kind) {
      case 'done':
        return tf('site.hist.done', { days: HISTORY_DAYS });
      case 'pending':
        return t('site.hist.pending');
      case 'loading':
        return tf('site.hist.loading', { done: hist.done, total: HISTORY_DAYS });
      case 'few':
        return tp('site.hist.few', hist.days);
      case 'error':
        return t('site.hist.error');
    }
  };
}

export function HistChip({ hist }: { hist: HistState }) {
  const { t } = useLocale();
  const text = useHistText();
  return (
    <span className={`chip${hist.kind === 'done' ? ' chip-minor' : ''}`}>
      <span className="k">{t('site.chip.hist')}</span>
      {text(hist)}
    </span>
  );
}

export function tariffKey(type: SiteResponse['tariff_type']): MessageKey {
  return type ? (`site.tariff.${type}` as MessageKey) : 'site.tariff.none';
}

export function SiteHeader({
  site,
  latest,
  hist,
  tab,
}: {
  site: SiteResponse;
  latest: SiteOverviewResponse['latest'] | undefined;
  hist: HistState | null;
  tab: 'overview' | 'settings';
}) {
  const { t, tf, locale } = useLocale();
  const { data: profile } = useProfile();
  const tz = site.timezone;
  const today = todayInTz(tz);
  const conn = connState(connOf(site), latest);
  const ident = tf('site.model', {
    model: modelLabel(site.model) ?? 'Deye',
    sn: serialOf(site.vendor_site_id),
  });
  const size =
    site.capacity_kwh !== null && site.p_max_kw !== null
      ? tf('site.model.size', {
          cap: fmtNumShort(locale, site.capacity_kwh),
          p: fmtNumShort(locale, site.p_max_kw),
        })
      : null;
  const overview = tab === 'overview';
  const region = regionOf(site.region)?.label[locale] ?? site.region;
  const osr = osrName(site.osr_code, locale);
  const pv = site.has_pv
    ? site.pv_kw_peak
      ? tf('site.chip.pv_kw', { kw: fmtNumShort(locale, site.pv_kw_peak) })
      : t('site.chip.pv_yes')
    : t('site.chip.pv_no');

  return (
    <section className="head" aria-labelledby="obj-title">
      <div className="crumbs">
        <Link to={homePath(profile?.role)}>{t('nav.objects')}</Link>
        <span aria-hidden="true">/</span>
        <span>{site.name}</span>
      </div>
      <div className="head-top">
        <div>
          <h1 id="obj-title">{site.name}</h1>
          <div className="model num">{overview && size ? `${ident} · ${size}` : ident}</div>
        </div>
        {overview ? (
          <span className={`chip${['off', 'pending', 'closed'].includes(conn) ? ' warn' : ''}`}>
            {conn === 'ok' && latest ? (
              <>
                <span className="dot" aria-hidden="true" />
                {tf('site.conn.ok_at', { time: fmtDateTime(latest.ts, tz) })}
              </>
            ) : null}
            {conn === 'off' && latest ? tf('site.conn.off', { time: fmtSince(latest.ts, tz, today) }) : null}
            {conn === 'pending' || conn === 'closed' || conn === 'noedit' ? t(`site.conn.${conn}`) : null}
            {conn === 'down' && site.vendor_down_since
              ? tf('site.conn.down', { time: fmtSince(site.vendor_down_since, tz, today) })
              : null}
            {conn === 'never' ? t('site.conn.never') : null}
          </span>
        ) : null}
      </div>
      {overview ? (
        <div className="chips">
          {region ? (
            <span className="chip">
              <span className="k">{t('site.chip.region')}</span>
              {region}
            </span>
          ) : null}
          {osr ? (
            <span className="chip chip-minor">
              <span className="k">{t('site.chip.osr')}</span>
              {osr}
            </span>
          ) : null}
          <span className="chip">
            <span className="k">{t('site.chip.tariff')}</span>
            {t(tariffKey(site.tariff_type))}
          </span>
          <span className="chip">
            <span className="k">{t('site.chip.pv')}</span>
            {pv}
          </span>
          {hist ? <HistChip hist={hist} /> : null}
          {site.phage_access.status === 'open' ? <PhageAccessChip site={site} /> : null}
        </div>
      ) : null}
      <nav className="tabs" aria-label={t('site.tabs')}>
        <Link to={`/app/sites/${site.id}`} aria-current={overview ? 'page' : undefined}>
          {t('site.tab.overview')}
        </Link>
        <Link to={`/app/sites/${site.id}/settings`} aria-current={overview ? undefined : 'page'}>
          {t('site.tab.settings')}
        </Link>
      </nav>
    </section>
  );
}
